import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge, RelationPicker } from './ui';
import { RELATION_CONFIGS } from './relationConfigs';
import { unwrapLookupItems, toInt } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldTextarea, FieldCustom, FieldToggle,
} from './detail/DetailShell';
import LocationPickerMap from './warehouse/LocationPickerMap';
import { usePageActions } from '../contexts/PermissionContext';
import { useFullRecordRefill } from './detail/useFullRecordRefill';
import { resolveTrAddress } from '../utils/trGeo'; // #769

const API_BASE = '/api/warehouses';

// Depo türü — etiket olarak saklanır (zorunlu). Tek doğruluk kaynağı backend:
// DataScope.Entities/Static/WarehouseTypes.cs (WarehouseTypes.UserSelectable).
// 'Transit Depo' formda SEÇİLEMEZ: sistem üretir (her Vendor için tekil, IsSystem=1).
const TYPE_OPTIONS = [
  'Ana Depo', 'Tali Depo', 'Mevzii Depo', 'Emanet Depo', 'Araç Depo', 'İade Deposu', 'Reklam Depo',
];

const STATUS_META = {
  1: { label: 'Aktif', tone: 'ok' },
  0: { label: 'Pasif', tone: 'neutral' },
  2: { label: 'Beklemede', tone: 'wait' }, // #769.1 eski etiket meta'da kalır (badge render için); dropdown'dan çıkarıldı
};
// #769.1 Durum dropdown'ından 'Beklemede' (value 2) kaldırıldı — yalnız Aktif/Pasif seçilebilir.
const STATUS_OPTIONS = Object.entries(STATUS_META)
  .filter(([value]) => Number(value) !== 2)
  .map(([value, meta]) => ({ value: Number(value), label: meta.label }));

const EMPTY_FORM = { textId: '', name: '', adress: '', description: '', erpId: '', status: 1, type: '', vendorId: '', latitude: null, longitude: null, isSystem: false, isDefault: false }; // #769.2 isDefault

// Backend decimal'i (string ya da number gelebilir) sayıya çevirir; geçersizse null.
function toNum(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }

function rowToForm(r) {
  return {
    textId: pick(r, 'textId', 'TextId') ?? '',
    name: pick(r, 'name', 'Name') ?? '',
    adress: pick(r, 'adress', 'Adress') ?? '',
    description: pick(r, 'description', 'Description') ?? '',
    erpId: pick(r, 'erpId', 'ErpId') ?? '',
    status: pick(r, 'status', 'Status') ?? '',
    type: pick(r, 'type', 'Type') ?? '',
    vendorId: pick(r, 'vendorId', 'VendorId') ?? '',
    latitude: toNum(pick(r, 'latitude', 'Latitude')),
    longitude: toNum(pick(r, 'longitude', 'Longitude')),
    isSystem: !!pick(r, 'isSystem', 'IsSystem'),
    isDefault: !!pick(r, 'isDefault', 'IsDefault'), // #769.2
  };
}

export function WarehouseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  usePageMeta(isNew ? 'Yeni Depo' : 'Depo Detayı', 'Depo kaydı bilgileri.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  // #769.4 Depo kullanımdaysa backend'in sorduğu "pasife alınsın mı?" metni (null = sorulmuyor).
  const [passivateAsk, setPassivateAsk] = useState(null);
  const [audit, setAudit] = useState(null);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  // #769.1 Kullanıcı forma dokunduysa arka plandaki tam-kayıt tazelemesi onu ezmesin.
  const { markDirty, refillIfClean } = useFullRecordRefill();
  const set = (k, v) => { markDirty(); setForm((c) => ({ ...c, [k]: v })); };

  // #769.3 Haritadan seçilen konumun reverse-geocode adresini Depo formunun Adres (adress) alanına aktarır.
  // Boş çözümlenen adres mevcut değeri EZMEZ. (CustomerEditPage.applyResolvedAddress deseni — depoda tek adres alanı var.)
  const applyResolvedAddress = (raw) => {
    const a = resolveTrAddress(raw);
    if (!a.fullAddress) return;
    setForm((c) => ({ ...c, adress: a.fullAddress }));
    showToast('success', 'Adres haritadan forma aktarıldı.');
  };

  // Sistem deposu (TRANSIT, IsSystem=1): kullanıcı DEĞİŞTİREMEZ / SİLEMEZ / KLONLAYAMAZ.
  // Backend zaten tür/kod/vendor değişimini yok sayar ve silmeyi reddeder; UI de kilitler.
  const isSystem = !isNew && !!form.isSystem;
  const canSave = (isNew ? canAdd : canEdit) && !isSystem;
  // Sistem deposunun türü ('Transit Depo') listede YOK → seçili değer boş görünmesin diye ekle.
  const typeOptions = isSystem && form.type && !TYPE_OPTIONS.includes(form.type)
    ? [{ value: form.type, label: form.type }]
    : [{ value: '', label: 'Seçiniz' }, ...TYPE_OPTIONS.map((t) => ({ value: t, label: t }))];

  const getVendorLabel = useCallback((vid) => {
    const v = vendors.find((item) => item.id === vid);
    if (!v) return '';
    return v.name ? `${v.textId || ''} - ${v.name}`.trim().replace(/^- /, '') : (v.textId || '');
  }, [vendors]);

  // Vendor (Distribütör) lookup
  useEffect(() => {
    fetch('/api/vendors/Select', { headers: authHeaders() })
      .then((res) => res.ok ? res.json() : [])
      .then((data) => setVendors(unwrapLookupItems(data)))
      .catch(() => {});
  }, []);

  // Tek kayıt yükleme: önce navigation state'ten gelen satır; yoksa GetById.
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    const stateRow = location.state?.row;
    if (stateRow && (stateRow.id === id || String(stateRow.id) === String(id))) {
      setForm(rowToForm(stateRow));
      // #769.1 Nav-state satırı liste OData $select'inden gelir → yalnız GÖRÜNÜR kolonlar.
      // Adres / Açıklama / Konum / Varsayılan Depo projeksiyonda olmadığı için formda BOŞ
      // görünüyordu (kayıt DB'de dolu olduğu hâlde). Tam kaydı NON-BLOCKING çekip formu ve
      // audit'i ondan tazeliyoruz.
      fetch(`${API_BASE}/GetById?Id=${encodeURIComponent(id)}`, { headers: authHeaders() })
        .then((res) => (res.ok ? res.json() : null))
        .then((body) => {
          const rec = unwrap(body);
          if (!rec) return;
          refillIfClean(() => setForm(rowToForm(rec)));
          setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
        })
        .catch(() => {});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/GetById?Id=${encodeURIComponent(id)}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Depo alınamadı (${res.status})`);
      const r = unwrap(await res.json());
      if (!r) throw new Error('Depo bulunamadı');
      setForm(rowToForm(r));
      setAudit({ createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(r, 'createdDate', 'CreatedDate'), updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    } catch (err) {
      showToast('error', err.message || 'Depo alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  // Klonla: /warehouses/new'e kopya veriyle gelindiyse formu doldur (kayıt OLUŞTURMA — kullanıcı Kaydet diyene kadar).
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const buildPayload = () => ({
    textId: form.textId, name: form.name, adress: form.adress || null, description: form.description || null,
    erpId: form.erpId || null, status: toInt(form.status), type: form.type, vendorId: form.vendorId,
    latitude: toNum(form.latitude), longitude: toNum(form.longitude),
    isDefault: !!form.isDefault, // #769.2 Varsayılan depo (backend CreateWarehouseRequest/WarehouseWriteRequest → IsDefault)
  });

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp boş yeni kayda hazırlan.
  // (önceki depo backend'e kaydedilmiş olur). Sadece yeni kayıt modunda anlamlı.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.textId.trim()) { showToast('error', 'Depo kodu boş olamaz'); return; }
    if (!form.name.trim()) { showToast('error', 'Depo adı boş olamaz'); return; }
    if (!form.vendorId) { showToast('error', 'Distribütör seçimi zorunludur'); return; }
    if (!form.type) { showToast('error', 'Tür seçimi zorunludur'); return; }
    // Transit deposu SİSTEM tarafından üretilir (Vendor başına tekil) → kullanıcı oluşturamaz.
    if (!TYPE_OPTIONS.includes(form.type)) { showToast('error', 'Bu depo türü kullanıcı tarafından oluşturulamaz'); return; }
    setSaving(true);
    try {
      const payload = buildPayload();
      const res = await fetch(`${API_BASE}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(isNew ? payload : { id, ...payload }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || (isNew ? 'Kayıt eklenemedi' : 'Kayıt güncellenemedi'));
      if (isNew) {
        if (andNew) {
          showToast('success', 'Depo eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Depo başarıyla eklendi');
        setTimeout(() => navigate('/warehouses'), 400);
        return;
      }
      showToast('success', 'Depo başarıyla güncellendi');
      setTimeout(() => navigate('/warehouses'), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut kaydın tüm form bilgilerini taşıyarak yeni-kayıt ekranını aç.
  // Hiçbir şey kaydedilmez; kullanıcı "Kaydet" diyene kadar yeni depo oluşmaz.
  const handleClone = () => {
    navigate('/warehouses/new', { state: { clone: { ...form } } });
  };

  /**
   * #769.4 (2. tur) İKİ AŞAMALI SİLME.
   *
   * İlk çağrı onaysızdır: depo kullanımdaysa backend hiçbir şey YAZMAZ, yalnız
   * `needsPassivateConfirm` ile "pasife alınsın mı?" sorusunu döner → kullanıcıya sorulur.
   * Eskiden onay sorulmadan pasife alınıyor ve sonuç kırmızı hata tonuyla bildiriliyordu; kullanıcı
   * hem işlem başarısız sanıyor hem de istemediği bir durum değişikliğiyle karşılaşıyordu.
   */
  const handleDelete = async (confirmPassivate = false) => {
    setSaving(true);
    try {
      const qs = confirmPassivate ? '?confirmPassivate=true' : '';
      const res = await fetch(`${API_BASE}/Delete${qs}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id }) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || 'Kayıt silinemedi');
      const data = pick(body, 'Data', 'data');

      // Kullanımda → kararı kullanıcı verir. Hiçbir şey değişmedi, ikinci onay istenir.
      if (data && (data.needsPassivateConfirm ?? data.NeedsPassivateConfirm)) {
        setPassivateAsk(pick(body, 'Message', 'message')
          || 'Bu depo kullanımda olduğu için silinemez. Pasife alınsın mı?');
        setDeleteConfirm(false);
        setSaving(false);
        return;
      }

      // Kullanıcı onayladı ve pasife alındı — bu bir hata değil, bilgi.
      if (data && (data.passivated ?? data.Passivated)) {
        showToast('info', pick(body, 'Message', 'message')
          || 'Depo silinmedi, pasife alındı.');
        set('status', data.status ?? data.Status ?? 0);
        setPassivateAsk(null);
        setDeleteConfirm(false);
        setSaving(false);
        return;
      }
      showToast('success', 'Depo başarıyla silindi');
      setTimeout(() => navigate('/warehouses'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme işlemi sırasında hata oluştu');
      setSaving(false);
      setDeleteConfirm(false);
      setPassivateAsk(null);
    }
  };

  const statusMeta = STATUS_META[form.status]; // #769 kullanılmayan `title` değişkeni kaldırıldı (eslint no-unused-vars temiz)
  const statusBadge = (
    <>
      {!isNew && statusMeta && <StatusBadge tone={statusMeta.tone}>{statusMeta.label}</StatusBadge>}
      {isSystem && <StatusBadge tone="bad">Sistem Deposu</StatusBadge>}
    </>
  );

  const actions = (
    <>
      {!isNew && canDelete && !isSystem && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/warehouses')} disabled={saving}>{isSystem ? 'Kapat' : 'İptal'}</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni depo aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Depolar', to: '/warehouses' }, { label: isNew ? 'Yeni Depo' : 'Depo Detayı', current: true }]}
      status={statusBadge}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'Warehouse', entityId: id }}
      loading={loading}
      readOnly={isSystem}
    >
      <div className="dps-form">
        {isSystem && (
          <div style={{ margin: '0 0 12px', padding: '10px 14px', borderRadius: 8, background: 'rgba(226, 68, 92, 0.08)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600 }}>
            Bu bir <strong>sistem deposudur</strong> (Transit). Depolar/araçlar arası transferde malın “yolda” beklediği ara depodur — düzenlenemez ve silinemez.
          </div>
        )}
        <Section icon="box" title="Depo Bilgileri" subtitle="Kod, ad, distribütör ve tür">
          <Grid cols={2}>
            <FieldText form={form} set={set} tried={tried} k="textId" label="Depo Kodu" req mono />
            <FieldText form={form} set={set} tried={tried} k="name" label="Depo Adı" req />
            <FieldCustom label="Distribütör" req invalid={tried && !form.vendorId}>
              <RelationPicker
                config={RELATION_CONFIGS.vendor}
                rows={vendors}
                value={form.vendorId}
                selectedLabel={form.vendorId ? getVendorLabel(form.vendorId) : ''}
                onChange={(val) => set('vendorId', val)}
                placeholder="Distribütör Seç"
              />
            </FieldCustom>
            <FieldSelect
              form={form} set={set} tried={tried} k="type" label="Tür" req
              options={typeOptions}
              disabled={isSystem}
              hint={isSystem ? 'Sistem deposunun türü değiştirilemez.' : undefined}
            />
            <FieldText form={form} set={set} k="erpId" label="ERP Kodu" />
            <FieldSelect form={form} set={set} k="status" label="Durum" options={[{ value: '', label: 'Seçiniz' }, ...STATUS_OPTIONS]} />
            {/* #769.2 Varsayılan Depo — bool; backend IsDefault alanına yazılır */}
            <FieldToggle form={form} set={set} k="isDefault" label="Varsayılan Depo" hint="Distribütör için varsayılan depo olarak işaretle." disabled={isSystem} />
          </Grid>
        </Section>

        <Section icon="mapPin" title="Adres & Açıklama" subtitle="Adres ve serbest açıklama">
          <Grid cols={2}>
            <FieldTextarea form={form} set={set} k="adress" label="Adres" rows={2} />
            <FieldTextarea form={form} set={set} k="description" label="Açıklama" rows={2} />
          </Grid>
        </Section>

        <Section icon="mapPin" title="Konum" subtitle="Depo konumunu harita üzerinde pinleyin — sevkiyat optimizasyonunda kullanılır">
          {/* #769.3 onAddress: konum seçilince Adres alanı otomatik dolar */}
          <LocationPickerMap
            lat={form.latitude}
            lng={form.longitude}
            onChange={(la, ln) => setForm((c) => ({ ...c, latitude: la, longitude: ln }))}
            onAddress={applyResolvedAddress}
          />
        </Section>
      </div>

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={() => handleDelete(false)} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.name}”</strong> deposunu silmek istediğinize emin misiniz?
          </p>
          {/* #769.2 Beklentiyi önden kur: hareketli depo silinmez, pasife alınması ayrıca sorulur. */}
          <p style={{ margin: '10px 0 0', lineHeight: 1.6, fontSize: 13, color: 'var(--text-secondary, #6b7280)' }}>
            Deponun hareketi varsa <strong>silinmez</strong>; geçmişi korumak için pasife alınması
            size ayrıca sorulur.
          </p>
        </SubModal>
      )}

      {/* #769.4 İkinci aşama: depo kullanımda. Pasife alma kararı kullanıcınındır. */}
      {passivateAsk && (
        <SubModal
          title="Depo Kullanımda"
          maxWidth={440}
          onClose={() => setPassivateAsk(null)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setPassivateAsk(null)} disabled={saving}>Vazgeç</button>
              <button className="dps-btn primary" onClick={() => handleDelete(true)} disabled={saving}>
                {saving ? 'Uygulanıyor…' : 'Evet, Pasife Al'}
              </button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>{passivateAsk}</p>
          <p style={{ margin: '10px 0 0', lineHeight: 1.6, fontSize: 13, color: 'var(--text-secondary, #6b7280)' }}>
            Pasife alınan depo listede kalır ve geçmiş hareketleri korunur; yeni belgelerde
            seçilemez. Vazgeçerseniz hiçbir şey değişmez.
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default WarehouseDetailPage;
